import React, { useState, useEffect } from 'react';
import { MobilityResponses, DomainResult } from '../../types/index';

interface MobilityAssessmentProps {
  onComplete: (result: DomainResult) => void;
  onNext: () => void;
}

const MobilityAssessment: React.FC<MobilityAssessmentProps> = ({ onComplete, onNext }) => {
  const [currentStep, setCurrentStep] = useState<'intro' | 'instructions' | 'safety_check' | 'test' | 'input_results' | 'results' | 'carer_correction' | 'completed'>('intro');
  const [responses, setResponses] = useState<MobilityResponses>({});
  const [testTimer, setTestTimer] = useState(0);
  const [isTestRunning, setIsTestRunning] = useState(false);
  const [inputRises, setInputRises] = useState<string>('');
  const [carerCorrection, setCarerCorrection] = useState({
    mobilityTestCorrect: null as boolean | null
  });

  // 评估逻辑实现
  const evaluateMobility = (responses: MobilityResponses): DomainResult => {
    // 处理跳过情况
    if (responses.skipped) {
      return {
        status: 'FAIL',
        completionMethod: 'skipped',
        skipReason: responses.skipReason,
        testResults: [{
          name: "椅子站立测试",
          passed: false,
          rawUserAnswers: {
            completedRises: 0,
            timeUsed: 0,
            skipped: true,
            testCompleted: false,
            skipReason: responses.skipReason
          },
          details: {
            completedRises: 0,
            timeUsed: 0,
            skipped: true,
            skipReason: responses.skipReason
          }
        }],
        recommendations: [
          "ICOPE-C3-S3.1.5-Community-Care-Overview",
          "ICOPE-C6-S6.2-CommunityExerciseIntervention"
        ]
      };
    }
    
    // 🔧 修复3: 数据边界条件验证
    const completedRises = responses.completedRises ?? 0;
    const timeUsed = responses.timeUsed ?? 0;

    // 验证数据有效性
    if (completedRises < 0) {
      console.warn('行动能力评估: 完成次数不能为负数');
      return {
        status: 'FAIL',
        completionMethod: 'full_test',
        notes: '数据异常：完成次数无效',
        recommendations: ["ICOPE-C3-S3.1.5-Community-Care-Overview"]
      };
    }

    if (timeUsed < 0) {
      console.warn('行动能力评估: 用时不能为负数');
      return {
        status: 'FAIL',
        completionMethod: 'full_test',
        notes: '数据异常：测试时间无效',
        recommendations: ["ICOPE-C3-S3.1.5-Community-Care-Overview"]
      };
    }

    // 限制最大值防止异常数据
    const validCompletedRises = Math.min(completedRises, 10); // 最多10次
    const validTimeUsed = Math.min(timeUsed, 300); // 最多5分钟

    if (completedRises > 10 || timeUsed > 300) {
      console.warn('行动能力评估: 数据超出合理范围，已进行修正');
    }
    
    // 评估测试结果 - 按照WHO ICOPE标准：≥5次为通过
    const completed = validCompletedRises >= 5;
    const withinTime = validTimeUsed <= 14;
    const passed = completed && withinTime;

    // 增强评估逻辑：部分完成情况的处理
    let assessmentNotes = '';
    if (!completed && validCompletedRises > 0) {
      assessmentNotes = `部分完成：完成了 ${validCompletedRises} 次（需要≥5次）`;
    }
    if (completed && !withinTime) {
      assessmentNotes = `完成了${validCompletedRises}次但超时：用时 ${validTimeUsed} 秒（标准≤14秒）`;
    }
    if (completed && withinTime && validCompletedRises > 5) {
      assessmentNotes = `优秀表现：完成了${validCompletedRises}次，用时 ${validTimeUsed} 秒`;
    }
    
    return {
      status: passed ? 'PASS' : 'FAIL',
      completionMethod: 'full_test',
      notes: assessmentNotes || undefined,
      testResults: [{
        name: "椅子站立测试",
        passed: passed,
        rawUserAnswers: {
          completedRises: validCompletedRises,
          timeUsed: validTimeUsed,
          skipped: false,
          testCompleted: completed
        },
        details: {
          completedRises: validCompletedRises,
          timeUsed: validTimeUsed,
          withinTimeLimit: withinTime,
          completedFullTest: completed,
          originalData: completedRises !== validCompletedRises || timeUsed !== validTimeUsed ? 
            { originalRises: completedRises, originalTime: timeUsed } : undefined
        }
      }],
      recommendations: passed ?
        ["ICOPE-C3-S3.1.3-Advice-PhysicalActivity"] :
        ["ICOPE-C3-S3.1.5-Community-Care-Overview"]
    };
  };

  const handleSafetyCheck = (isSafe: boolean) => {
    if (!isSafe) {
      const skipResponses = {
        skipped: true,
        skipReason: "出于安全考虑跳过测试"
      };
      setResponses(skipResponses);
      const result = evaluateMobility(skipResponses);
      onComplete(result);
      setCurrentStep('completed');
    } else {
      setCurrentStep('test');
    }
  };

  const renderIntro = () => (
    <div className="text-center space-y-6 p-6">
      <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-gray-900">行动能力评估</h2>
      <p className="text-gray-600 max-w-md mx-auto">
        我们将通过椅子起立测试来评估您的行动能力。这是一个简单安全的测试，可以反映您的腿部力量和平衡能力。
      </p>
      <button 
        onClick={() => setCurrentStep('instructions')} 
        className="w-full bg-orange-600 text-white py-3 px-6 rounded-lg hover:bg-orange-700 transition-colors font-medium"
      >
        开始评估
      </button>
    </div>
  );

  const startTest = () => {
    setIsTestRunning(true);
    setTestTimer(0);
    
    // 开始14秒倒计时
    const startTime = Date.now();
    const timer = setInterval(() => {
      const elapsed = (Date.now() - startTime) / 1000;
      setTestTimer(elapsed);
      
      if (elapsed >= 14) {
        // 14秒时间到，自动停止
        clearInterval(timer);
        finishTest();
      }
    }, 100);
    
    // 存储计时器ID以便清理
    (window as any).mobilityTimer = timer;
  };

  const finishTest = () => {
    setIsTestRunning(false);
    
    if ((window as any).mobilityTimer) {
      clearInterval((window as any).mobilityTimer);
    }
    
    // 跳转到输入结果步骤
    setCurrentStep('input_results');
  };

  const handleResultInput = () => {
    const completedRises = parseInt(inputRises) || 0;
    
    const testResponses = {
      skipped: false,
      completedRises: completedRises,
      timeUsed: 14, // 固定14秒
      testCompleted: completedRises >= 5
    };
    
    setResponses(testResponses);
    setCurrentStep('results');
  };

  const handleResultsConfirm = () => {
    const result = evaluateMobility(responses);
    onComplete(result);
    setCurrentStep('completed');
  };

  const handleCarerCorrection = (mobilityCorrect: boolean) => {
    // 基于照护者修正重新评估椅子起立测试结果
    const correctedResult: DomainResult = {
      status: mobilityCorrect ? 'PASS' : 'FAIL',
      completionMethod: 'carer_corrected',
      testResults: [{
        name: "椅子站立测试",
        passed: mobilityCorrect,
        rawUserAnswers: {
          completedRises: responses.completedRises || 0,
          timeUsed: responses.timeUsed || 0,
          skipped: false,
          testCompleted: responses.testCompleted || false,
          carerCorrected: true
        },
        details: {
          completedRises: responses.completedRises || 0,
          timeUsed: responses.timeUsed || 0,
          carerCorrected: true,
          originalSystemResult: evaluateMobility(responses).status
        }
      }],
      recommendations: mobilityCorrect ?
        ["ICOPE-C3-S3.1.3-Advice-PhysicalActivity"] :
        ["ICOPE-C3-S3.1.5-Community-Care-Overview"]
    };
    
    onComplete(correctedResult);
    setCurrentStep('completed');
  };

  useEffect(() => {
    if (currentStep === 'completed') {
      const timer = setTimeout(() => {
        onNext();
      }, 2000);
      return () => clearTimeout(timer);
    }
    
    // 清理计时器
    return () => {
      if ((window as any).mobilityTimer) {
        clearInterval((window as any).mobilityTimer);
      }
    };
  }, [currentStep]);

  const renderInstructions = () => (
    <div className="p-6 space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">椅子起立测试指南</h2>
        <p className="text-gray-600">请仔细阅读以下说明，确保测试安全有效</p>
      </div>
      
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
        <h3 className="font-semibold text-gray-900 mb-4">📋 测试步骤：</h3>
        <div className="space-y-4">
          <div className="flex items-start space-x-4">
            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-orange-600 font-bold">1</span>
            </div>
            <div>
              <h4 className="font-medium text-gray-900">准备姿势</h4>
              <p className="text-sm text-gray-700">坐在椅子上，双脚平放在地面，双臂交叉放在胸前</p>
            </div>
          </div>
          <div className="flex items-start space-x-4">
            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-orange-600 font-bold">2</span>
            </div>
            <div>
              <h4 className="font-medium text-gray-900">执行动作</h4>
              <p className="text-sm text-gray-700">不使用手臂支撑，完全站起然后完全坐下，至少重复5次</p>
            </div>
          </div>
          <div className="flex items-start space-x-4">
            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-orange-600 font-bold">3</span>
            </div>
            <div>
              <h4 className="font-medium text-gray-900">时间要求</h4>
              <p className="text-sm text-gray-700">尽可能快但安全地完成，标准是14秒内完成</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* 标准动作示意图 - 使用您的原图 */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="font-semibold text-gray-900 mb-4 text-center">🎯 标准动作示意</h3>
        
        <div className="flex justify-center mb-4">
          <img 
            src="/images/assessment/chair_rise_sequence.png" 
            alt="椅子起立测试标准动作示意图：坐姿准备 → 完全站起 → 完全坐下，至少重复5次" 
            className="max-w-full h-auto rounded-lg shadow-sm"
            style={{ maxHeight: '200px' }}
            onError={(e) => {
              // 如果图片加载失败，显示提示信息
              const target = e.currentTarget as HTMLImageElement;
              target.style.display = 'none';
              const nextElement = target.nextElementSibling as HTMLElement;
              if (nextElement) {
                nextElement.style.display = 'block';
              }
            }}
          />
          <div className="text-center p-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 hidden">
            <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 48 48">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 24v-6a9 9 0 1 1 18 0v6m-9 6v6m-12-6h24m-12-6V12m0 18v6" />
            </svg>
            <p className="text-sm text-gray-600 font-medium mb-2">椅子起立测试动作图示</p>
            <p className="text-xs text-gray-500 mb-3">
              请将椅子起立测试的标准动作图保存为：<br/>
              <code className="bg-gray-200 px-2 py-1 rounded">/public/images/assessment/chair_rise_sequence.png</code>
            </p>
            <div className="text-xs text-gray-400 space-y-1">
              <p>动作流程：坐姿准备 → 完全站起 → 完全坐下</p>
              <p>重复：至少完成5次完整动作</p>
            </div>
          </div>
        </div>

        <div className="text-center bg-gray-50 rounded-lg p-3">
          <p className="text-sm text-gray-600">
            <span className="font-medium">动作要领：</span>
            双臂交叉胸前 → 不用手支撑完全站起 → 完全坐下 → 至少重复5次
          </p>
        </div>
      </div>
      
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start space-x-2">
          <svg className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <div>
            <p className="font-medium text-yellow-800">安全提醒</p>
            <p className="text-yellow-700 text-sm mt-1">
              如果您感到不适、头晕或担心安全，请随时停止测试。确保椅子稳固，周围有足够空间。
            </p>
          </div>
        </div>
      </div>
      
      <button 
        onClick={() => setCurrentStep('safety_check')} 
        className="w-full bg-orange-600 text-white py-3 px-6 rounded-lg hover:bg-orange-700 transition-colors font-medium"
      >
        我已理解说明，继续安全检查
      </button>
    </div>
  );

  const renderSafetyCheck = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">安全确认</h2>
        <p className="text-gray-600">在开始测试前，请确认安全条件</p>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <h3 className="font-semibold text-gray-900 mb-4">请确认以下条件：</h3>
        
        <div className="space-y-3 mb-6">
          <div className="flex items-center space-x-3">
            <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span className="text-gray-700">椅子稳固且靠墙</span>
          </div>
          <div className="flex items-center space-x-3">
            <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span className="text-gray-700">周围有足够空间</span>
          </div>
          <div className="flex items-center space-x-3">
            <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span className="text-gray-700">有人陪同或在附近</span>
          </div>
          <div className="flex items-center space-x-3">
            <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span className="text-gray-700">身体感觉良好，无明显不适</span>
          </div>
        </div>
        
        <div className="space-y-3">
          <button
            onClick={() => handleSafetyCheck(true)}
            className="w-full p-4 text-left bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
          >
            <span className="font-medium text-green-800">是的，我已准备好，可以安全进行测试</span>
          </button>
          
          <button
            onClick={() => handleSafetyCheck(false)}
            className="w-full p-4 text-left bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
          >
            <span className="font-medium text-red-800">不，出于安全考虑我想跳过这个测试</span>
          </button>
        </div>
      </div>
    </div>
  );

  const renderTest = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">椅子站立测试</h2>
        <p className="text-gray-600">
          {!isTestRunning ? "准备开始测试" : "测试进行中"}
        </p>
      </div>

      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        {!isTestRunning ? (
          <div className="text-center">
            <div className="mb-6">
              <div className="text-4xl font-bold text-green-600 mb-2">5次</div>
              <p className="text-gray-700">完整的站立-坐下动作</p>
            </div>
            
            <div className="mb-6">
              <div className="text-2xl font-bold text-gray-900 mb-2">14秒</div>
              <p className="text-gray-600">目标完成时间</p>
            </div>
            
            <button
              onClick={startTest}
              className="bg-green-600 text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-green-700 transition-colors"
            >
              开始测试
            </button>
          </div>
        ) : (
          <div className="text-center">
            <div className="mb-6">
              <div className="text-6xl font-bold text-green-600 mb-2">
                {(14 - testTimer).toFixed(1)}
              </div>
              <p className="text-gray-700">秒剩余</p>
            </div>
            
            <div className="mb-6">
              <div className="text-3xl font-bold text-gray-900 mb-2">
                专心做测试
              </div>
              <p className="text-gray-600">在14秒内尽力完成椅子站立动作</p>
            </div>
            
            {/* 动态视频指导 */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-blue-800 mb-3 text-center">🎥 动作指导视频</h4>
              
              <div className="flex justify-center mb-3">
                <div className={`relative rounded-lg overflow-hidden shadow-lg transition-all duration-500 ${
                  Math.floor(testTimer) % 3 === 0 ? 'ring-2 ring-orange-400' : 
                  Math.floor(testTimer) % 3 === 1 ? 'ring-2 ring-green-400' : 'ring-2 ring-blue-400'
                }`}>
                  <video 
                    src="/images/assessment/chair_rise_demonstration.mp4"
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="max-w-full h-auto"
                    style={{ maxHeight: '150px' }}
                    onError={(e) => {
                      // 视频加载失败时，回退到图片
                      const target = e.currentTarget as HTMLVideoElement;
                      target.style.display = 'none';
                      const nextElement = target.nextElementSibling as HTMLElement;
                      if (nextElement) {
                        nextElement.style.display = 'block';
                      }
                    }}
                  >
                    您的浏览器不支持视频播放。
                  </video>
                  
                  {/* 视频回退：显示静态图片 */}
                  <img 
                    src="/images/assessment/chair_rise_sequence.png" 
                    alt="椅子起立测试动作指导图片" 
                    className="max-w-full h-auto hidden"
                    style={{ maxHeight: '150px' }}
                    onError={(e) => {
                      // 图片也加载失败时，显示文字提示
                      const target = e.currentTarget as HTMLImageElement;
                      target.style.display = 'none';
                      const nextElement = target.nextElementSibling as HTMLElement;
                      if (nextElement) {
                        nextElement.style.display = 'block';
                      }
                    }}
                  />
                  
                  {/* 最终回退：文字提示 */}
                  <div className="hidden bg-gray-100 p-6 text-center">
                    <svg className="w-12 h-12 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 48 48">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M9 18v-7a2 2 0 012-2h1m0 0V6a2 2 0 012-2h2a2 2 0 012 2v3M9 18v7a2 2 0 002 2h10a2 2 0 002-2v-7M9 18h12" />
                    </svg>
                    <p className="text-sm text-gray-600">请添加动作指导视频</p>
                    <p className="text-xs text-gray-500 mt-1">文件路径：/public/images/assessment/<br/>chair_rise_demonstration.mp4</p>
                  </div>
                </div>
              </div>
              
              <div className="text-center mb-3">
                <p className="text-sm text-blue-800 font-medium">
                  📺 跟随视频节奏进行测试
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  当前动作：{Math.floor(testTimer) % 4 < 2 ? '📉 坐下' : '📈 站起'}
                </p>
              </div>
              
              {/* 视频控制提示 */}
              <div className="flex justify-center space-x-4 mb-3">
                <div className="flex items-center space-x-2 text-xs text-blue-700">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                  </svg>
                  <span>自动循环播放</span>
                </div>
                <div className="flex items-center space-x-2 text-xs text-blue-700">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.793l-4-3a1 1 0 010-1.586l4-3a1 1 0 01.617-.207zM15.383 3.076A1 1 0 0116 4v12a1 1 0 01-1.617.793l-4-3a1 1 0 010-1.586l4-3a1 1 0 01.617-.207z" clipRule="evenodd" />
                  </svg>
                  <span>静音播放</span>
                </div>
              </div>
              
              <ul className="text-xs text-blue-700 space-y-1">
                <li>• 双臂交叉放在胸前，不用手支撑</li>
                <li>• 完全站直，然后完全坐下</li>
                <li>• 跟随视频节奏，保持安全</li>
                <li>• 如感到不适或不安全，立即停止</li>
              </ul>
            </div>
            
            {/* 动态步骤提示 */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex justify-center space-x-4">
                <div className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
                  Math.floor(testTimer) % 4 < 2 ? 'bg-orange-100 border border-orange-200' : 'bg-gray-100'
                }`}>
                  <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                  <span className="text-sm font-medium">坐下</span>
                </div>
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
                <div className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
                  Math.floor(testTimer) % 4 >= 2 ? 'bg-green-100 border border-green-200' : 'bg-gray-100'
                }`}>
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="text-sm font-medium">站起</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderInputResults = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">测试完成</h2>
        <p className="text-gray-600">请输入您在14秒内完成的椅子站立次数</p>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">请回想一下：</h3>
          <p className="text-gray-700 mb-4">
            在刚才的14秒内，您一共完成了多少次<strong>完整的</strong>椅子站立动作？
          </p>
          <div className="text-sm text-gray-600 space-y-1 mb-4">
            <p>• 完整动作：从坐下状态完全站直，然后重新坐下</p>
            <p>• 只计算完全完成的次数，不包括未完成的动作</p>
            <p>• 如果不确定，请保守估计</p>
          </div>
        </div>
        
        <div className="mb-6">
          <label className="block text-lg font-medium text-gray-900 mb-4">
            完成次数：
          </label>
          <div className="flex items-center space-x-4">
            <input
              type="number"
              min="0"
              max="10"
              value={inputRises}
              onChange={(e) => setInputRises(e.target.value)}
              placeholder="0"
              className="w-20 p-3 border border-gray-300 rounded-lg text-xl text-center font-bold"
            />
            <span className="text-lg text-gray-600">次</span>
          </div>
        </div>
        
        <div className="grid grid-cols-6 gap-2 mb-6">
          {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
            <button
              key={num}
              onClick={() => setInputRises(num.toString())}
              className={`p-3 rounded-lg border-2 text-lg font-semibold transition-all ${
                inputRises === num.toString()
                  ? 'bg-blue-100 border-blue-500 text-blue-700'
                  : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
              }`}
            >
              {num}
            </button>
          ))}
        </div>
        
        <button
          onClick={handleResultInput}
          disabled={inputRises === ''}
          className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 transition-colors"
        >
          确认次数
        </button>
      </div>
    </div>
  );

  const renderResults = () => {
    const passed = (responses.completedRises || 0) >= 5 && (responses.timeUsed || 0) <= 14;
    
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
            passed ? 'bg-green-100' : 'bg-yellow-100'
          }`}>
            <svg className={`w-8 h-8 ${passed ? 'text-green-600' : 'text-yellow-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">测试结果</h2>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">{responses.completedRises}</div>
              <p className="text-gray-600">完成次数</p>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">{(responses.timeUsed || 0).toFixed(1)}秒</div>
              <p className="text-gray-600">使用时间</p>
            </div>
          </div>
          
          <div className={`p-4 rounded-lg ${
            passed ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'
          }`}>
            <div className="flex items-center space-x-2">
              <svg className={`w-5 h-5 ${passed ? 'text-green-600' : 'text-yellow-600'}`} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className={`font-medium ${passed ? 'text-green-800' : 'text-yellow-800'}`}>
                {passed ? '测试通过' : '测试未完全通过'}
              </span>
            </div>
            <p className={`mt-2 text-sm ${passed ? 'text-green-700' : 'text-yellow-700'}`}>
              {passed 
                ? '您的行动能力表现良好，请继续保持规律运动' 
                : '建议加强安全的体育锻炼，改善行动能力'}
            </p>
          </div>
          
          <div className="mt-6 flex flex-col gap-3">
            <button
              onClick={handleResultsConfirm}
              className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              确认结果，完成测试
            </button>
            <button
              onClick={() => setCurrentStep('carer_correction')}
              className="w-full bg-orange-500 text-white py-3 px-6 rounded-lg hover:bg-orange-600 transition-colors font-medium flex items-center justify-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span>照护者修正答案</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderCarerCorrection = () => {
    const systemResult = evaluateMobility(responses);
    const passed = systemResult.status === 'PASS';
    
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">照护者答案确认</h2>
          <p className="text-gray-600">请照护者根据实际情况确认答案正确性</p>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">系统评估结果回顾</h3>
            
            <div className="bg-white rounded-lg p-4 border border-gray-200 mb-4">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="text-center">
                  <div className="text-xl font-bold text-gray-900">{responses.completedRises}</div>
                  <p className="text-sm text-gray-600">完成次数</p>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-gray-900">{(responses.timeUsed || 0).toFixed(1)}秒</div>
                  <p className="text-sm text-gray-600">使用时间</p>
                </div>
              </div>
              
              <div className={`p-3 rounded-lg text-center ${passed ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <span className={`font-medium ${passed ? 'text-green-800' : 'text-red-800'}`}>
                  系统评估：{passed ? '✅ 通过 (≥5次且≤14秒)' : '❌ 未通过 (<5次或>14秒)'}
                </span>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">照护者确认</h3>
            <p className="text-gray-700 mb-4">
              请照护者根据观察到的实际情况，确认老年人的行动能力状况：
            </p>
            
            <div className="space-y-3">
              <button
                onClick={() => setCarerCorrection({ mobilityTestCorrect: true })}
                className={`w-full p-4 text-left rounded-lg border-2 transition-all ${
                  carerCorrection.mobilityTestCorrect === true
                    ? 'bg-green-50 border-green-300 text-green-800'
                    : 'bg-white border-gray-200 hover:border-green-300 text-gray-700'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      carerCorrection.mobilityTestCorrect === true ? 'bg-green-100 border-green-400' : 'border-gray-300'
                    }`}>
                      {carerCorrection.mobilityTestCorrect === true && (
                        <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <div>
                      <span className="font-medium text-green-700">✅ 行动能力正常</span>
                      <p className="text-sm text-green-600 mt-1">
                        能够顺利完成椅子起立动作，腿部力量和平衡能力良好
                      </p>
                    </div>
                  </div>
                </div>
              </button>
              
              <button
                onClick={() => setCarerCorrection({ mobilityTestCorrect: false })}
                className={`w-full p-4 text-left rounded-lg border-2 transition-all ${
                  carerCorrection.mobilityTestCorrect === false
                    ? 'bg-red-50 border-red-300 text-red-800'
                    : 'bg-white border-gray-200 hover:border-red-300 text-gray-700'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      carerCorrection.mobilityTestCorrect === false ? 'bg-red-100 border-red-400' : 'border-gray-300'
                    }`}>
                      {carerCorrection.mobilityTestCorrect === false && (
                        <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <div>
                      <span className="font-medium text-red-700">❌ 行动能力有困难</span>
                      <p className="text-sm text-red-600 mt-1">
                        椅子起立动作有困难，腿部力量不足或平衡问题
                      </p>
                    </div>
                  </div>
                </div>
              </button>
            </div>
          </div>
          
          <button
            onClick={() => handleCarerCorrection(carerCorrection.mobilityTestCorrect!)}
            disabled={carerCorrection.mobilityTestCorrect === null}
            className="w-full bg-orange-600 text-white py-3 rounded-lg hover:bg-orange-700 disabled:bg-gray-300 transition-colors font-medium"
          >
            确认修正，完成评估
          </button>
        </div>
      </div>
    );
  };

  const renderCompleted = () => (
    <div className="text-center space-y-4">
      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-gray-900">行动能力评估完成</h2>
      <p className="text-gray-600">正在进入下一项评估...</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto">
        {currentStep === 'intro' && renderIntro()}
        {currentStep === 'instructions' && renderInstructions()}
        {currentStep === 'safety_check' && renderSafetyCheck()}
        {currentStep === 'test' && renderTest()}
        {currentStep === 'input_results' && renderInputResults()}
        {currentStep === 'results' && renderResults()}
        {currentStep === 'carer_correction' && renderCarerCorrection()}
        {currentStep === 'completed' && renderCompleted()}
      </div>
    </div>
  );
};

export default MobilityAssessment;